import { useMemo, useState } from "react";
import {
  X,
  Trash2,
  Eye,
  Sun,
  Moon,
  Pipette,
  Copy,
  ArrowRightLeft,
  Lock,
  LockOpen,
} from "lucide-react";
import { putFetcher } from "api/swr";
import useSWRMutation from "swr/mutation";
import { toast } from "@/components/ui/use-toast";
import ReactGPicker from "react-gcolor-picker";
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";

// 检查浏览器是否支持 EyeDropper API
const supportsEyeDropper =
  typeof window !== "undefined" && "EyeDropper" in window;

// 从 ColorSet 中提取 light/dark 颜色（兼容新旧格式）
function extractColors(colorSet: any): { light: string; dark: string } {
  // 优先使用新格式
  if (colorSet.light && colorSet.dark) {
    return { light: colorSet.light, dark: colorSet.dark };
  }

  // 兼容旧格式
  if (colorSet.colors && colorSet.colors.length > 0) {
    const sorted = [...colorSet.colors].sort(
      (a: any, b: any) => a.offset - b.offset
    );
    const lightStop = sorted.find((s: any) => s.offset <= 0.1) || sorted[0];
    const darkStop =
      sorted.find((s: any) => s.offset >= 0.9) || sorted[sorted.length - 1];
    return {
      light: lightStop?.color || "#ffffff",
      dark: darkStop?.color || "#000000",
    };
  }

  return { light: "#ffffff", dark: "#000000" };
}

// 渐变预览组件
function GradientPreview({ light, dark }: { light: string; dark: string }) {
  const gradientStyle = useMemo(
    () => ({
      background: `linear-gradient(to right, ${light} 0%, ${dark} 100%)`,
    }),
    [light, dark]
  );

  return (
    <div className="space-y-1">
      <div className="w-full h-10 rounded-lg border" style={gradientStyle} />
      <div className="flex justify-between text-[10px] font-medium px-1 text-muted-foreground">
        <span className="text-amber-600">Light (0%)</span>
        <span className="text-slate-500">Dark (100%)</span>
      </div>
    </div>
  );
}

// 颜色编辑器组件
interface ColorEditorProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  onChange: (color: string) => void;
  className?: string;
}

function ColorEditor({
  label,
  icon,
  color,
  onChange,
  className,
}: ColorEditorProps) {
  const [open, setOpen] = useState(false);

  const handleEyeDropper = async () => {
    if (!supportsEyeDropper) return;
    try {
      // @ts-ignore
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      onChange(result.sRGBHex);
    } catch (e) {
      // 用户取消
    }
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${className}`}>
      <div className="flex items-center gap-2 w-16 shrink-0">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>

      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            className="w-12 h-10 rounded-lg border-2 cursor-pointer transition-all hover:scale-105 hover:shadow-md"
            style={{ backgroundColor: color }}
          />
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-50 w-auto rounded-md border bg-popover p-3 shadow-md"
            align="start"
            sideOffset={4}
          >
            <ReactGPicker
              value={color}
              onChange={(v) => onChange(v)}
              gradient={false}
              solid
              debounceMS={10}
              format="hex"
              showAlpha={false}
              showInputs
              defaultColors={[
                "#ffffff",
                "#f5f5f5",
                "#e0e0e0",
                "#9e9e9e",
                "#616161",
                "#212121",
                "#000000",
                "#ffcdd2",
                "#ef9a9a",
                "#e57373",
                "#ef5350",
                "#f44336",
                "#e53935",
                "#c62828",
                "#f8bbd9",
                "#f48fb1",
                "#f06292",
                "#ec407a",
                "#e91e63",
                "#d81b60",
                "#ad1457",
                "#e1bee7",
                "#ce93d8",
                "#ba68c8",
                "#ab47bc",
                "#9c27b0",
                "#8e24aa",
                "#6a1b9a",
                "#c5cae9",
                "#9fa8da",
                "#7986cb",
                "#5c6bc0",
                "#3f51b5",
                "#3949ab",
                "#283593",
                "#bbdefb",
                "#90caf9",
                "#64b5f6",
                "#42a5f5",
                "#2196f3",
                "#1e88e5",
                "#1565c0",
                "#b2ebf2",
                "#80deea",
                "#4dd0e1",
                "#26c6da",
                "#00bcd4",
                "#00acc1",
                "#00838f",
                "#c8e6c9",
                "#a5d6a7",
                "#81c784",
                "#66bb6a",
                "#4caf50",
                "#43a047",
                "#2e7d32",
                "#fff9c4",
                "#fff59d",
                "#fff176",
                "#ffee58",
                "#ffeb3b",
                "#fdd835",
                "#f9a825",
                "#ffe0b2",
                "#ffcc80",
                "#ffb74d",
                "#ffa726",
                "#ff9800",
                "#fb8c00",
                "#ef6c00",
                "#d7ccc8",
                "#bcaaa4",
                "#a1887f",
                "#8d6e63",
                "#795548",
                "#6d4c41",
                "#4e342e",
              ]}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {supportsEyeDropper && (
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={handleEyeDropper}
          title="吸色笔"
        >
          <Pipette className="h-4 w-4" />
        </Button>
      )}

      <Input
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-sm font-mono flex-1"
        placeholder="#000000"
      />
    </div>
  );
}

// 生成随机 ID
function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

export function ColorSetDetailPanel() {
  const {
    templateConfig,
  } = useConfigStore();
  const {
    editingToningId,
    editingColorSetId,
    setEditingColorSetId,
    selectedToningColors,
    setToningColor,
    updateColorSet,
    deleteColorSet,
    addColorSet,
    moveColorSet,
  } = useEditorStore();

  const toning = templateConfig?.tonings.find((t) => t.id === editingToningId);
  const colorSet = toning?.colors.find((c) => c.id === editingColorSetId);

  if (!toning || !colorSet) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-lg">配色详情</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground text-center">
            点击具体配色查看详情
          </p>
        </CardContent>
      </Card>
    );
  }

  const isCurrentPreview = selectedToningColors[toning.id] === colorSet.id;
  const requiresAd = colorSet.requireAd === 1;

  const { trigger: updateRequireAdRemote, isMutating: isUpdatingRequireAd } =
    useSWRMutation(`/spine-tonings/${toning.id}/colors/require-ad`, putFetcher);

  // 提取当前 light/dark 值（兼容新旧格式）
  const { light: lightColor, dark: darkColor } = extractColors(colorSet);

  // 检测是否有旧格式数据
  const hasLegacyData = colorSet.colors && colorSet.colors.length > 0;
  const legacyColors = hasLegacyData
    ? [...colorSet.colors].sort((a: any, b: any) => a.offset - b.offset)
    : [];

  // 检测是否已有新格式数据（light/dark）
  const hasNewFormatData = !!colorSet.light && !!colorSet.dark;

  // 更新 Light 颜色（同时确保 dark 存在）
  const handleLightColorChange = (color: string) => {
    updateColorSet(toning.id, colorSet.id, {
      light: color,
      dark: colorSet.dark || darkColor, // 确保 dark 也有值
    });
  };

  // 更新 Dark 颜色（同时确保 light 存在）
  const handleDarkColorChange = (color: string) => {
    updateColorSet(toning.id, colorSet.id, {
      light: colorSet.light || lightColor, // 确保 light 也有值
      dark: color,
    });
  };

  const handleDelete = () => {
    deleteColorSet(toning.id, colorSet.id);
    setEditingColorSetId(toning.id, null);
  };

  const handleCopy = () => {
    const newColorSet = {
      id: generateId(),
      light: lightColor,
      dark: darkColor,
    };
    addColorSet(toning.id, newColorSet);
    // 自动选中新复制的配色
    setEditingColorSetId(toning.id, newColorSet.id);
  };

  const handleSetPreview = () => {
    setToningColor(toning.id, colorSet.id);
    useEditorStore.getState().setPreviewToningId(toning.id);
  };

  const handleToggleRequireAd = async () => {
    const nextRequireAd = requiresAd ? 0 : 1;
    updateColorSet(toning.id, colorSet.id, { requireAd: nextRequireAd });
    try {
      await updateRequireAdRemote({
        colorId: colorSet.id,
        requireAd: nextRequireAd,
      });
      toast({
        title: nextRequireAd === 1 ? "已标记为看广告解锁" : "已取消看广告解锁",
      });
    } catch {
      updateColorSet(toning.id, colorSet.id, {
        requireAd: requiresAd ? 1 : 0,
      });
      toast({
        variant: "destructive",
        title: "更新失败",
        description: "请稍后重试或保存模板后重试",
      });
    }
  };

  // 删除旧格式数据
  const handleDeleteLegacyData = () => {
    updateColorSet(toning.id, colorSet.id, {
      light: colorSet.light || lightColor,
      dark: colorSet.dark || darkColor,
      colors: [], // 清空旧数据
    });
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">配色详情</CardTitle>
            <span className="text-sm text-muted-foreground font-mono">
              {colorSet.id}
            </span>
            {isCurrentPreview && (
              <Badge variant="default" className="shrink-0">
                <Eye className="h-3 w-3 mr-1" />
                预览中
              </Badge>
            )}
            {requiresAd && (
              <Badge variant="secondary" className="shrink-0 text-[10px]">
                AD
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setEditingColorSetId(editingToningId, null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        {/* 渐变预览 */}
        <div className="space-y-2">
          <Label className="text-xs">效果预览</Label>
          <GradientPreview light={lightColor} dark={darkColor} />
        </div>

        {/* 设为预览按钮 */}
        {!isCurrentPreview && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleSetPreview}
          >
            <Eye className="h-3 w-3 mr-1" />
            设为预览
          </Button>
        )}

        {/* Two Color Tint 编辑 */}
        <div className="space-y-2">
          <Label className="text-xs">Two Color Tint</Label>

          <ColorEditor
            label="Light"
            icon={<Sun className="h-4 w-4 text-amber-500" />}
            color={lightColor}
            onChange={handleLightColorChange}
            className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
          />

          <ColorEditor
            label="Dark"
            icon={<Moon className="h-4 w-4 text-slate-500" />}
            color={darkColor}
            onChange={handleDarkColorChange}
            className="bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-600"
          />
        </div>

        {/* 旧格式数据 - 供选择 */}
        {hasLegacyData && legacyColors.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 space-y-3">
            <div className="flex items-start gap-2">
              <span className="text-amber-600 dark:text-amber-400 text-lg">
                📦
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  旧数据（{legacyColors.length} 个色点）
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  {hasNewFormatData
                    ? "已有新数据，可删除旧数据"
                    : "点击颜色选择设置为 Light 或 Dark"}
                </p>
              </div>
              {hasNewFormatData && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-7 px-2 text-xs shrink-0"
                  onClick={handleDeleteLegacyData}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  删除旧数据
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {legacyColors.map((c: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 rounded bg-white dark:bg-black/20"
                >
                  <div
                    className="w-8 h-8 rounded border-2 shadow-sm shrink-0"
                    style={{ backgroundColor: c.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs">{c.color}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {Math.round(c.offset * 100)}%
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-amber-600 border-amber-300 hover:bg-amber-100"
                      onClick={() => handleLightColorChange(c.color)}
                    >
                      <Sun className="h-3 w-3 mr-1" />
                      Light
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-slate-600 border-slate-300 hover:bg-slate-100"
                      onClick={() => handleDarkColorChange(c.color)}
                    >
                      <Moon className="h-3 w-3 mr-1" />
                      Dark
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 所属方案 */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">所属调色方案</Label>
          <Input
            value={toning.name || toning.id}
            disabled
            className="text-sm bg-muted h-8"
          />
        </div>

        {/* 看广告解锁 */}
        <Button
          variant={requiresAd ? "secondary" : "outline"}
          size="sm"
          className="w-full"
          onClick={handleToggleRequireAd}
          disabled={isUpdatingRequireAd}
        >
          {requiresAd ? (
            <>
              <LockOpen className="h-3 w-3 mr-1" />
              取消看广告解锁
            </>
          ) : (
            <>
              <Lock className="h-3 w-3 mr-1" />
              标记为看广告解锁
            </>
          )}
        </Button>

        {/* 复制按钮 */}
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleCopy}
        >
          <Copy className="h-3 w-3 mr-1" />
          复制此配色
        </Button>

        {/* 移动到其他调色方案 */}
        {templateConfig?.tonings.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <ArrowRightLeft className="h-3 w-3 mr-1" />
                移动到其他方案
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-48">
              {templateConfig?.tonings
                .filter((t: any) => t.id !== toning.id)
                .map((t: any) => (
                  <DropdownMenuItem
                    key={t.id}
                    onClick={() => {
                      moveColorSet(toning.id, t.id, colorSet.id);
                      setEditingColorSetId(null, null);
                    }}
                  >
                    {t.name || t.id}
                    <Badge variant="secondary" className="ml-auto text-[10px]">
                      {t.colors?.length || 0}
                    </Badge>
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* 删除按钮 */}
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={handleDelete}
          disabled={toning.colors.length <= 1}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          删除此配色
        </Button>
        {toning.colors.length <= 1 && (
          <p className="text-[10px] text-muted-foreground text-center">
            调色方案至少需要保留一个配色
          </p>
        )}
      </CardContent>
    </Card>
  );
}
